<template>
    <PrimeVueDialog
        v-for="modal in activeModals"
        :key="modal.id"
        v-model:visible="modal.visible"
        :header="modal.header"
        :style="getDialogStyle(modal)"
        :pt="getDialogPt(modal)"
        :class="modal.customClass"
        modal
        :draggable="!modal.fullScreen"
        :closable="modal.canCancel?.button !== false"
        :close-on-escape="modal.canCancel?.escape !== false"
        :dismissable-mask="modal.canCancel?.outside !== false"
        block-scroll
        append-to="body"
        @hide="handleModalHide(modal)"
        @after-hide="handleAfterHide(modal)"
    >
        <!-- Dynamically render the component -->
        <component
            v-bind="modal.props"
            :is="modal.component"
            v-on="modal.events"
            @close="closeModal(modal.id)"
        />
    </PrimeVueDialog>
</template>

<script>
import { usePrimeVue } from 'primevue/config';
import PrimeVueDialog from 'primevue/dialog';
import { computed, ref } from 'vue';
import { ModalService } from '@/services/ModalService.js';

export default {
    name: 'GlobalModals',
    components: {
        PrimeVueDialog,
    },
    setup() {
        const modalIds = ref([]);

        // Get active modals from ModalService
        const activeModals = computed(() => {
            return modalIds.value.map(id => ModalService.getModal(id)).filter(modal => modal !== undefined);
        });

        const handleModalOpen = event => {
            const { id } = event.detail || {};

            if (!id) {
                console.error('GlobalModals: No modal ID provided');
                return;
            }

            // Verify the modal exists in ModalService
            const modal = ModalService.getModal(id);
            if (!modal) {
                console.error('GlobalModals: Modal not found in ModalService', id);
                return;
            }

            // Add to our list of active modal IDs
            if (!modalIds.value.includes(id)) {
                modalIds.value.push(id);
            }
        };

        const removeModalId = id => {
            const index = modalIds.value.indexOf(id);
            if (index !== -1) {
                modalIds.value.splice(index, 1);
            }
        };

        const handleModalClose = event => {
            const { id } = event.detail;
            removeModalId(id);
        };

        const handleModalCloseAll = () => {
            modalIds.value = [];
        };

        const closeModal = id => {
            const modal = ModalService.getModal(id);
            if (modal) {
                modal.visible = false;
            }
        };

        const getDialogStyle = modal => {
            if (!modal.fullScreen) {
                return { width: modal.width };
            }

            return {
                width: '100vw',
                height: '100vh',
                maxWidth: '100vw',
                maxHeight: '100vh',
                margin: '0',
                borderRadius: '0',
            };
        };

        const getDialogPt = modal => {
            if (!modal.fullScreen) {
                return undefined;
            }

            return {
                mask: {
                    style: {
                        alignItems: 'stretch',
                        justifyContent: 'stretch',
                    },
                },
                root: {
                    style: {
                        margin: '0',
                        borderRadius: '0',
                    },
                },
                content: {
                    style: {
                        height: '100%',
                        padding: '0',
                    },
                },
            };
        };

        const handleModalHide = modal => {
            if (modal.onCancel) {
                modal.onCancel();
            }
        };

        const handleAfterHide = modal => {
            // Remove from our tracking
            removeModalId(modal.id);
        };

        return {
            activeModals,
            handleModalOpen,
            handleModalClose,
            handleModalCloseAll,
            closeModal,
            getDialogStyle,
            getDialogPt,
            handleModalHide,
            handleAfterHide,
        };
    },
    mounted() {
        window.addEventListener('primevue-modal-open', this.handleModalOpen);
        window.addEventListener('primevue-modal-close', this.handleModalClose);
        window.addEventListener('primevue-modal-close-all', this.handleModalCloseAll);
    },
    beforeUnmount() {
        window.removeEventListener('primevue-modal-open', this.handleModalOpen);
        window.removeEventListener('primevue-modal-close', this.handleModalClose);
        window.removeEventListener('primevue-modal-close-all', this.handleModalCloseAll);
    },
};
</script>

<style scoped>
/* Additional modal styling if needed */
</style>
